import { Link } from "react-router-dom";
import { FaUsers, FaChalkboardTeacher, FaCertificate, FaChartLine, FaBrain, FaDatabase, FaCheckCircle, FaArrowRight, FaBuilding, FaRocket, FaLightbulb, FaHandshake, FaCog, FaGlobe, FaCode, FaWindows, FaCloud, FaVideo } from "react-icons/fa";
import medialogo1 from "../../assets/image/edo.png";
import medialogo2 from "../../assets/image/edo_ministry.png";
import medialogo3 from "../../assets/image/esosa.png";
import medialogo4 from "../../assets/image/google.png";
import medialogo5 from "../../assets/image/pic.png";
import "./OrganizationLanding.css";

const curriculum = [
  {
    icon: <FaBrain />,
    title: "AI for Business & Innovation",
    desc: "Leveraging AI technologies to drive innovation, optimize operations, support strategic decision-making, and improve business performance.",
  },
  {
    icon: <FaDatabase />,
    title: "Data Science & Analytics",
    desc: "Data wrangling, visualization, statistical analysis, and business intelligence storytelling for data-driven decisions.",
  },
  {
    icon: <FaCode />,
    title: "Prompt Engineering for Professionals",
    desc: "Crafting effective prompts to maximize the quality, accuracy, and usefulness of AI-generated outputs.",
  },
  {
    icon: <FaCloud />,
    title: "Google Workspace Productivity & Administration",
    desc: "Master Gmail, Drive, Docs, Sheets, and Slides while learning user management, security settings, permissions, and organizational administration.",
  },
  {
    icon: <FaWindows />,
    title: "Microsoft 365 Productivity Suite",
    desc: "Enhance workplace efficiency using Teams, Outlook, Word, Excel, and PowerPoint for communication, documentation, and collaboration.",
  },
  {
    icon: <FaVideo />,
    title: "Virtual Collaboration & Remote Work Tools",
    desc: "Develop effective remote collaboration skills using Zoom and other digital communication tools for meetings, webinars, teamwork, and hybrid work environments.",
  },
];

const keyOfferings = [
  {
    icon: <FaRocket />,
    title: "Increased Employee Productivity",
    desc: "Equip teams with practical digital and AI skills that improve efficiency and day-to-day performance.",
  },
  {
    icon: <FaLightbulb />,
    title: "Better Decision-Making",
    desc: "Use data, analytics, and reporting tools to support smarter and more informed business decisions.",
  },
  {
    icon: <FaHandshake />,
    title: "Improved Team Collaboration",
    desc: "Enable seamless communication and teamwork through modern workplace systems and shared workflows.",
  },
  {
    icon: <FaCog />,
    title: "Reduced Manual Work",
    desc: "Automate repetitive processes and routine tasks to save time, reduce errors, and increase efficiency.",
  },
  {
    icon: <FaGlobe />,
    title: "Future-Ready Workforce",
    desc: "Develop the digital, data, and AI capabilities employees need to adapt and succeed in a rapidly evolving workplace.",
  },
];

const benefits = [
  {
    icon: <FaUsers />,
    title: "Team-Wide Enrollment",
    desc: "Enroll your entire team under one organizational account with a single dashboard view.",
  },
  {
    icon: <FaChalkboardTeacher />,
    title: "Live Instructor-Led Training",
    desc: "Expert-led live sessions tailored to your industry context, not generic slides.",
  },
  {
    icon: <FaChartLine />,
    title: "Progress Tracking Dashboard",
    desc: "Monitor each employee's learning journey, assignments, and performance in real time.",
  },
  {
    icon: <FaCertificate />,
    title: "Verified Certificates",
    desc: "Every staff member earns a professional GritinAI certificate to showcase their skills.",
  },
  {
    icon: <FaBrain />,
    title: "AI-Powered Mentorship",
    desc: "24/7 AI tutor support keeps your team learning beyond scheduled class hours.",
  },
  {
    icon: <FaBuilding />,
    title: "Custom Curriculum",
    desc: "We shape the curriculum around your sector, goals, and existing technical baseline.",
  },
];

const steps = [
  {
    number: "01",
    title: "Tell Us Your Goals",
    desc: "Fill in a short brief about your organization's size, technical baseline, and desired outcomes.",
  },
  {
    number: "02",
    title: "We Design Your Program",
    desc: "Our curriculum team crafts a bespoke training plan aligned with your industry and timeline.",
  },
  {
    number: "03",
    title: "Your Team Trains Live",
    desc: "Cohort-based or rolling sessions — your staff learns from expert instructors in a structured program.",
  },
  {
    number: "04",
    title: "Track, Certify & Scale",
    desc: "Monitor progress via your org dashboard, issue certificates, and expand training as you grow.",
  },
];

const partners = [
  { src: medialogo1, alt: "Data Science Nigeria", href: "https://datasciencenigeria.org/" },
  { src: medialogo2, alt: "Edo Ministry", href: "https://edo.ng/" },
  { src: medialogo3, alt: "Esosa Food Systems", href: "https://esosafoodsystems.com/" },
  { src: medialogo4, alt: "Google Developer Group", href: "https://gdg.community.dev/gdg-benin/" },
  { src: medialogo5, alt: "Policy Centre", href: "https://policycentre.org/" },
  { src: "/logo.png", alt: "GritinAI", href: "https://gritinai.com/" },
];

const OrganizationLanding = () => {
  return (
    <div className="org-landing">

      {/* ── HERO ── */}
      <section className="graduated-projects-hero">
        <div className="graduated-projects-hero-grid" aria-hidden="true" />
        <div className="graduated-projects-hero-content">
          <div className="graduated-projects-hero-badge">
            <span className="badge-dot" />
            For Organizations
          </div>
          <h1 className="graduated-projects-hero-title">
            Build an AI-Ready <span>Workforce.</span>
          </h1>
          <p className="graduated-projects-hero-subtitle">
            From hands-on cohort training to fully custom programs, GritinAI equips your teams with the AI and machine learning skills they need to lead in today's fast-moving world.
          </p>
          <div className="org-hero-ctas mt-4">
            <Link to="/organization/register" className="org-cta-primary">
              Register Your Organization
              <FaArrowRight className="org-cta-icon" />
            </Link>
            <Link to="/contact" className="org-cta-secondary">
              Request Custom Training
            </Link>
          </div>
        </div>
      </section>

      {/* ── STATS STRIP ── */}
      <section className="org-stats-strip">
        <div className="container">
          <div className="org-stats-grid">
            {[
              { value: "15", label: "Professionals Trained", icon: <FaUsers />, desc: "Upskilled in modern AI" },
              { value: "2", label: "Organizations Served", icon: <FaBuilding />, desc: "Across diverse sectors" },
              { value: "10+", label: "AI Courses Available", icon: <FaBrain />, desc: "Led by industry experts" },
              { value: "95%", label: "Satisfaction Rate", icon: <FaChartLine />, desc: "Highly rated programs" },
            ].map((stat) => (
              <div className="org-stat-item" key={stat.label}>
                <div className="org-stat-card-accent" />
                <div className="org-stat-icon-wrapper">{stat.icon}</div>
                <div className="org-stat-info">
                  <span className="org-stat-value">{stat.value}</span>
                  <span className="org-stat-label">{stat.label}</span>
                  <span className="org-stat-desc">{stat.desc}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW WE EMPOWER YOUR ORG ── */}
      <section className="org-empower">
        <div className="container">
          <div className="org-section-header">
            <span className="org-section-tag">The Platform</span>
            <h2>How Organizations Leverage<br />the GritinAI Hub</h2>
            <p>
              GritinAI isn't just a training provider — it's a full learning infrastructure for your organization. Here's what your team gets access to from day one.
            </p>
          </div>
          <div className="org-empower-grid">
            <div className="org-empower-card">
              <div className="org-empower-num">01</div>
              <h3>Enroll Your Entire Team</h3>
              <p>
                Create an organizational account, add your team members, and get everyone learning under one dashboard. No more scattered logins or fragmented progress.
              </p>
            </div>
            <div className="org-empower-card">
              <div className="org-empower-num">02</div>
              <h3>Live Cohorts & Custom Training</h3>
              <p>
                Enroll your team in structured, scheduled AI and ML cohorts led by industry experts, or request a custom-tailored training program designed for your company's unique needs.
              </p>
            </div>
            <div className="org-empower-card">
              <div className="org-empower-num">03</div>
              <h3>Assign Courses to Teams</h3>
              <p>
                Organize your workforce by assigning different courses and specialized learning paths to different departments or teams based on their specific roles.
              </p>
            </div>
            <div className="org-empower-card">
              <div className="org-empower-num">04</div>
              <h3>Track Every Step</h3>
              <p>
                Monitor real-time progress, quiz and assignment scores, attendance, and certification status for every single employee from your dashboard.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── KEY OFFERINGS ── */}
      <section className="org-key-offerings">
        <div className="container">
          <div className="org-section-header">
            <span className="org-section-tag">Key Offerings</span>
            <h2>Benefits for Your Organization</h2>
            <p>
              Investing in your team's digital and AI skills delivers measurable results across every layer of your business.
            </p>
          </div>
          <div className="org-offerings-grid">
            {keyOfferings.map((item) => (
              <div className="org-offering-card" key={item.title}>
                <div className="org-offering-icon">{item.icon}</div>
                <div className="org-offering-text">
                  <h3>{item.title}</h3>
                  <p>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CUSTOM TRAINING ── */}
      <section className="org-custom">
        <div className="container">
          <div className="org-custom-inner">
            <div className="org-custom-text">
              <span className="org-section-tag">Custom Training</span>
              <h2>A Program Built Around<br />Your Organization</h2>
              <p>
                Generic training rarely sticks. We sit with your leadership team to understand your context — your industry, your team's skill gaps, your business objectives — and design a program that delivers measurable results.
              </p>
              <ul className="org-custom-features">
                {[
                  "Industry-specific case studies and projects",
                  "Flexible scheduling to fit your work calendar",
                  "Dedicated instructor for your organization",
                  "Private cohort or blended learning options",
                  "Post-training support and AI mentorship access",
                ].map((f) => (
                  <li key={f}>
                    <FaCheckCircle className="org-check-icon" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Link to="/contact" className="org-cta-primary-solid">
                Request Custom Training
                <FaArrowRight className="org-cta-icon" />
              </Link>
            </div>
            <div className="org-custom-steps">
              {steps.map((step) => (
                <div className="org-step-card" key={step.number}>
                  <div className="org-step-number">{step.number}</div>
                  <div>
                    <h4>{step.title}</h4>
                    <p>{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── WHAT WE TEACH ── */}
      <section className="org-curriculum">
        <div className="container">
          <div className="org-section-header">
            <span className="org-section-tag">Curriculum</span>
            <h2>What We Equip Teams With</h2>
            <p>
              From AI and data skills to productivity tools and remote collaboration, our programs cover the full range of capabilities modern organizations need.
            </p>
          </div>
          <div className="org-curriculum-grid">
            {curriculum.map((item) => (
              <div className="org-curriculum-card" key={item.title}>
                <div className="org-curriculum-icon">{item.icon}</div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── BENEFITS ── */}
      <section className="org-benefits">
        <div className="container">
          <div className="org-section-header light">
            <span className="org-section-tag light">Why GritinAI</span>
            <h2>The Benefits of Training<br />with GritinAI</h2>
            <p>
              We've built every feature of our platform with organizational teams in mind, from enrollment to certification.
            </p>
          </div>
          <div className="org-benefits-grid">
            {benefits.map((b) => (
              <div className="org-benefit-card" key={b.title}>
                <div className="org-benefit-icon">{b.icon}</div>
                <h3>{b.title}</h3>
                <p>{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── PARTNERS / ALUMNI ORGS ── */}
      <section className="org-partners">
        <div className="container">
          <div className="org-section-header">
            <span className="org-section-tag">Trusted By</span>
            <h2>Organizations That Have<br />Leveraged GritinAI</h2>
            <p>
              Leading institutions across Nigeria and Africa have partnered with us to equip their teams and communities with cutting-edge AI skills.
            </p>
          </div>
          <div className="org-partners-grid">
            {partners.map((p) => (
              <a key={p.alt} href={p.href} target="_blank" rel="noopener noreferrer" className="org-partner-item">
                <img src={p.src} alt={p.alt} />
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="org-final-cta">
        <div className="container">
          <div className="org-final-cta-inner">
            <h2>Ready to Build Your<br />AI-Powered Team?</h2>
            <p>
              Join the organizations already transforming their workforce through GritinAI's expert-led training. Register today or talk to us about a custom program.
            </p>
            <div className="org-hero-ctas">
              <Link to="/organization/register" className="org-cta-primary">
                Register Your Organization
                <FaArrowRight className="org-cta-icon" />
              </Link>
              <Link to="/contact" className="org-cta-secondary light">
                Talk to Us First
              </Link>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default OrganizationLanding;
